import { Injectable } from "@angular/core";
import { environment } from "projects/ert/src/environments/environment";
import { Observable, forkJoin, of, BehaviorSubject } from "rxjs";
import { IPicklistFieldsData } from "../../../interfaces/administration/picklistFieldsData";
import { HttpClient } from "@angular/common/http";
import { IPicklistValuesData } from "../../../interfaces/administration/picklistValuesData";
import { LoggedUserService } from "../../common/logged-user.service";
import { CacheService } from "../../common/cache.service";
import { switchMap, take, tap, map } from "rxjs/operators";

@Injectable({
  providedIn: "root",
})
export class PicklistService {
  private apiUrl = environment.apiUrl;
  private facilityId: number = 0;
  private facilityIdSubject = new BehaviorSubject<number>(0);
  private facilityIdInitialized = false;
  private cachePrefix = "ert_picklist_";

  constructor(
    private http: HttpClient,
    private currentUser: LoggedUserService,
    private cacheService: CacheService
  ) {
    this.initializeFacilityId();
  }

  private initializeFacilityId(): void {
    this.currentUser.getLoggedUserData().subscribe((data) => {
      if (data && data.facilityId) {
        const facilityId = Number(data.facilityId);
        this.facilityId = facilityId;
        this.facilityIdSubject.next(facilityId);
        this.facilityIdInitialized = true;
      }
    });
  }

  private ensureFacilityId(): Observable<number> {
    if (this.facilityIdInitialized && this.facilityId > 0) {
      return of(this.facilityId);
    }

    return this.currentUser.getLoggedUserData().pipe(
      take(1),
      tap((data) => {
        if (data && data.facilityId) {
          this.facilityId = Number(data.facilityId);
          this.facilityIdInitialized = true;
        }
      }),
      switchMap(() => of(this.facilityId))
    );
  }

  /**
   * Generate cache key for picklist data
   */
  private generateCacheKey(
    type: string,
    facilityId: Number,
    additionalKey?: string | Number
  ): string {
    return this.cacheService.generateCacheKey(
      this.cachePrefix,
      `${type}_${facilityId}`,
      additionalKey
    );
  }

  /**
   * Clear all cached picklist data
   */
  public clearCache(): void {
    this.cacheService.clearCacheWithPrefix(this.cachePrefix);
  }

  public getAllPicklistsFields(): Observable<IPicklistFieldsData[]> {
    return this.ensureFacilityId().pipe(
      switchMap((facilityId) => {
        const cacheKey = this.generateCacheKey("fields_all", facilityId);
        const cachedData =
          this.cacheService.getCacheData<IPicklistFieldsData[]>(cacheKey);

        if (cachedData) {
          return of(cachedData);
        }

        const url = `${this.apiUrl}/picklist/${facilityId}/fields`;
        return this.http
          .get<IPicklistFieldsData[]>(url)
          .pipe(tap((data) => this.cacheService.setCacheData(cacheKey, data)));
      })
    );
  }

  public getPicklistValues(fieldId: number): Observable<IPicklistValuesData[]> {
    return this.ensureFacilityId().pipe(
      switchMap((facilityId) => {
        const cacheKey = this.generateCacheKey("values", facilityId, fieldId);
        const cachedData =
          this.cacheService.getCacheData<IPicklistValuesData[]>(cacheKey);

        if (cachedData) {
          return of(cachedData);
        }

        const url = `${this.apiUrl}/picklist/${facilityId}/values/${fieldId}`;
        return this.http
          .get<IPicklistValuesData[]>(url)
          .pipe(tap((data) => this.cacheService.setCacheData(cacheKey, data)));
      })
    );
  }

  public getPicklistValuesByParent(
    parentId: number
  ): Observable<IPicklistValuesData[]> {
    return this.ensureFacilityId().pipe(
      switchMap((facilityId) => {
        const cacheKey = this.generateCacheKey(
          "values_parent",
          facilityId,
          parentId
        );
        const cachedData =
          this.cacheService.getCacheData<IPicklistValuesData[]>(cacheKey);

        if (cachedData) {
          return of(cachedData);
        }

        const url = `${this.apiUrl}/picklist/${facilityId}/values/parent/${parentId}`;
        return this.http
          .get<IPicklistValuesData[]>(url)
          .pipe(tap((data) => this.cacheService.setCacheData(cacheKey, data)));
      })
    );
  }

  public createField(
    fieldName: string,
    parentFieldId: number,
    facilityId?: number
  ): Observable<IPicklistFieldsData> {
    const url = `${this.apiUrl}/picklist/fields`;
    return this.ensureFacilityId().pipe(
      switchMap((currentFacilityId) => {
        var obj: IPicklistFieldsData = {
          id: 0,
          name: fieldName,
          parentPicklistFieldsId:
            parentFieldId && parentFieldId > 0 ? parentFieldId : null,
          facilityId: facilityId || currentFacilityId,
        };

        return this.http.post<IPicklistFieldsData>(url, obj).pipe(
          tap(() => {
            // Clear relevant cached data since we've modified fields
            this.clearCacheForFacility(facilityId || currentFacilityId);
          })
        );
      })
    );
  }

  /**
   * Clear cache only for a specific facility
   */
  private clearCacheForFacility(facilityId: number): void {
    this.cacheService.clearCacheWithPrefixAndKey(this.cachePrefix, facilityId);
  }

  public createValue(
    value: string,
    isOnPrem: boolean,
    fieldId: number,
    parentId?: number,
    facilityId?: number
  ): Observable<IPicklistValuesData> {
    const url = `${this.apiUrl}/picklist/values/${fieldId}`;
    return this.ensureFacilityId().pipe(
      switchMap((currentFacilityId) => {
        var obj: IPicklistValuesData = {
          id: 0,
          value: value,
          isOnPrem: isOnPrem,
          picklistFieldId: fieldId,
          parentId: parentId,
          facilityId: facilityId || currentFacilityId,
        };

        return this.http.post<IPicklistValuesData>(url, obj).pipe(
          tap(() => {
            // Clear entire picklist cache to simplify the process
            this.clearCache();
          })
        );
      })
    );
  }

  public createMultipleValues(
    values: string[],
    fieldId: number,
    parentId?: number,
    isOnPrem?: boolean,
    facilityId?: number
  ): Observable<IPicklistValuesData[]> {
    // Filter out empty values
    const validValues = values.filter((val) => val && val.trim() !== "");
    isOnPrem = isOnPrem || false;
    if (validValues.length === 0) {
      return of([]);
    }

    return this.ensureFacilityId().pipe(
      switchMap((currentFacilityId) => {
        // Create observables for each value creation
        const createObservables = validValues.map((value) =>
          this.createValue(
            value.trim(),
            isOnPrem,
            fieldId,
            parentId,
            facilityId || currentFacilityId
          )
        );

        // Execute all requests in parallel and return combined results
        return forkJoin(createObservables).pipe(
          tap(() => {
            // Clear relevant cached data
            const finalFacilityId = facilityId || currentFacilityId;
            const cacheKey = this.generateCacheKey(
              "values",
              finalFacilityId,
              fieldId
            );
            this.cacheService.removeCacheItem(cacheKey);
          })
        );
      })
    );
  }

  public updateField(
    updatedField: IPicklistFieldsData
  ): Observable<IPicklistFieldsData> {
    const url = `${this.apiUrl}/picklist/fields`;
    return this.http.put<IPicklistFieldsData>(url, updatedField).pipe(
      tap(() => {
        // Clear relevant cache after update
        this.clearCacheForFacility(updatedField.facilityId);
      })
    );
  }

  public updateValue(
    updatedValue: IPicklistValuesData
  ): Observable<IPicklistValuesData> {
    const url = `${this.apiUrl}/picklist/values/${updatedValue.picklistFieldId}`;
    return this.http.put<IPicklistValuesData>(url, updatedValue).pipe(
      tap(() => {
        // Clear relevant cache
        const cacheKey = this.generateCacheKey(
          "values",
          updatedValue.facilityId,
          updatedValue.picklistFieldId
        );
        this.cacheService.removeCacheItem(cacheKey);
      })
    );
  }

  public deleteField(id: number): Observable<boolean> {
    return this.ensureFacilityId().pipe(
      switchMap((facilityId) => {
        const url = `${this.apiUrl}/picklist/fields/${id}`;
        return this.http.delete<boolean>(url).pipe(
          tap(() => {
            // Clear facility cache since fields structure changed
            this.clearCacheForFacility(facilityId);
          })
        );
      })
    );
  }

  public deleteValue(
    fieldId: Number,
    value: IPicklistValuesData
  ): Observable<boolean> {
    const url = `${this.apiUrl}/picklist/values/${fieldId}/${value.id}`;
    return this.http.delete<boolean>(url).pipe(
      tap(() => {
        this.ensureFacilityId().subscribe((facilityId) => {
          // Clear relevant cache
          const cacheKey = this.generateCacheKey("values", facilityId, fieldId);
          this.cacheService.removeCacheItem(cacheKey);
        });
      })
    );
  }

  public getPicklistValuesByFieldName(
    fieldName: string
  ): Observable<IPicklistValuesData[]> {
    return this.ensureFacilityId().pipe(
      switchMap((facilityId) => {
        const cacheKey = this.generateCacheKey(
          "values_fieldname",
          facilityId,
          fieldName
        );
        const cachedData =
          this.cacheService.getCacheData<IPicklistValuesData[]>(cacheKey);

        if (cachedData) {
          return of(cachedData);
        }

        const url = `${this.apiUrl}/picklist/${facilityId}/values/field/${fieldName}`;
        return this.http
          .get<IPicklistValuesData[]>(url)
          .pipe(tap((data) => this.cacheService.setCacheData(cacheKey, data)));
      })
    );
  }

  public getFieldIdByName(fieldName: string): Observable<number> {
    return this.ensureFacilityId().pipe(
      switchMap((facilityId) => {
        const cacheKey = this.generateCacheKey(
          "fieldid_byname",
          facilityId,
          fieldName
        );
        const cachedData = this.cacheService.getCacheData<number>(cacheKey);

        if (cachedData !== null) {
          return of(cachedData);
        }

        const url = `${this.apiUrl}/picklist/${facilityId}/fields/${fieldName}`;
        return this.http
          .get<number>(url)
          .pipe(tap((data) => this.cacheService.setCacheData(cacheKey, data)));
      })
    );
  }
}
